import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSBanner, setShowIOSBanner] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // Only show on mobile/tablet devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) return;

    // Don't show if already dismissed recently (7 days)
    const dismissed = localStorage.getItem("install_prompt_dismissed");
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Don't show if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return;

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari detection
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);
    if (isIOS && isSafari) {
      setShowIOSBanner(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === "accepted") {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSBanner(false);
    localStorage.setItem("install_prompt_dismissed", Date.now().toString());
  };

  if (!showBanner && !showIOSBanner) return null;

  return (
    <div style={styles.banner}>
      <div style={styles.content}>
        <img
          src="/logo192.png"
          alt="Pethub"
          style={styles.icon}
        />
        <div style={styles.text}>
          <strong style={styles.title}>Pethub</strong>
          {showIOSBanner ? (
            <span style={styles.subtitle}>
              {t("install_ios_hint") !== "install_ios_hint"
                ? t("install_ios_hint")
                : "Tap Share → Add to Home Screen"}
            </span>
          ) : (
            <span style={styles.subtitle}>
              {t("install_app_hint") !== "install_app_hint"
                ? t("install_app_hint")
                : "Install app for quick access"}
            </span>
          )}
        </div>
      </div>
      <div style={styles.actions}>
        {showBanner && (
          <button style={styles.installBtn} onClick={handleInstall}>
            {t("install") !== "install" ? t("install") : "Install"}
          </button>
        )}
        <button style={styles.closeBtn} onClick={handleDismiss}>
          ✕
        </button>
      </div>
    </div>
  );
};

const styles = {
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    background: "#fff",
    borderBottom: "1px solid #eee",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    zIndex: 9999,
    position: "sticky",
    top: 0,
    gap: "12px",
  },
  content: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
  },
  icon: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    flexShrink: 0,
  },
  text: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  title: {
    fontSize: "14px",
    color: "#333",
  },
  subtitle: {
    fontSize: "12px",
    color: "#888",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  installBtn: {
    background: "#F57C00",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "18px",
    color: "#999",
    cursor: "pointer",
    padding: "4px",
    lineHeight: 1,
  },
};

export default InstallPrompt;
